/**
 * What one page says about another.
 *
 * A wiki is a tree plus a web: the tree is `parent_id` and `sort_order`, and
 * this is the web. `[[Onboarding]]` in the middle of a sentence points at the
 * page called Onboarding — by **title**, not by id, because a link somebody
 * types has to be readable in the source and survive being pasted into a chat
 * message. The id is what the renderer produces; the title is what the author
 * wrote.
 *
 * Everything here is pure and takes the pages as an argument, for the reason
 * `pagetree.ts` is: the client already holds every page it may read, so
 * backlinks are arithmetic over a list rather than a round trip, and the
 * arithmetic is worth proving without a browser. The same functions answer for
 * MCP on the server, over rows out of SQLite.
 *
 * One thing is deliberately *not* here: a link table. A link lives in the text
 * that spells it, and a row saying the same thing is a second thing to keep
 * true — one that goes stale the first time a body is written by a path that
 * forgot about it. The graph is built from the text on demand instead, which is
 * cheap because the text is already in hand on both sides.
 */

/** One `[[…]]` in a page, and where in the source it sits. */
export interface WikiLink {
  /** The title as written, trimmed. `[[ Onboarding | how we start ]]` → `Onboarding`. */
  target: string;
  /**
   * The section, when the link named one: `[[Onboarding#Day one]]` → `Day one`.
   *
   * A leading `#` means *this* page — `[[#Day one]]` — and leaves `target`
   * empty, which is Obsidian's reading and worth matching because it is the one
   * people arrive with. The cost is that a page literally called `#done` cannot
   * be linked to by that name, and that is the cheaper of the two losses.
   */
  heading: string | null;
  /** What to show instead of the title, when the author gave one. */
  label: string | null;
  /** Offsets into the source, so a rewrite can splice rather than re-parse. */
  at: number;
  end: number;
}

/** The least a page has to say to take part in the web. */
export interface LinkablePage {
  id: string;
  title: string;
  content?: string | null;
  /** Ties are broken by age, so a resolution does not flip when a page is edited. */
  created_at?: number;
}

/**
 * The comparable form of a title.
 *
 * Case and runs of whitespace are noise — somebody typing `[[design review]]`
 * on a phone means the page called `Design Review` — and a title is trimmed
 * before it is stored anyway. Nothing else is folded: `Q1` and `q-1` are two
 * different pages and pretending otherwise would silently join them.
 */
export const pageKey = (title: string): string => title.trim().replace(/\s+/g, ' ').toLowerCase();

/**
 * Whether a title can be the target of a link at all.
 *
 * `[[`, `]]` and `|` are the syntax, so a title containing one of them cannot
 * be written inside it — the link would end early and the rest would be prose.
 * Said out loud rather than escaped: an escape somebody has to know about is a
 * worse trap than a title they can see is unusual, and the interface can offer
 * the id instead when this is false.
 */
export const linkableTitle = (title: string): boolean => !/[[\]|\n]/.test(title);

/**
 * The source with everything the renderer treats as code blanked out, offsets
 * intact.
 *
 * Blanked rather than removed, so an offset into the result is an offset into
 * the original — which is what lets `renameLinks` splice the real string. The
 * two shapes are the two the renderer knows: a fenced block, and a code span.
 * Four-space indented code is not a code block here for the same reason it is
 * not one there.
 */
export function withoutCode(source: string): string {
  const lines = source.split('\n');
  let fence: string | null = null;
  const kept = lines.map((line) => {
    const open = /^\s*(`{3,}|~{3,})/.exec(line);
    if (fence) {
      const close = /^\s*(`{3,}|~{3,})\s*$/.exec(line);
      if (close && close[1][0] === fence[0] && close[1].length >= fence.length) fence = null;
      return ' '.repeat(line.length);
    }
    if (open) {
      fence = open[1];
      return ' '.repeat(line.length);
    }
    return line;
  });
  // Code spans after the fences, and only on the lines that survived them: a
  // stray backtick inside a fenced block must not pair with one outside it.
  return kept.join('\n').replace(/`[^`\n]+`/g, (span) => ' '.repeat(span.length));
}

/**
 * `[[target]]`, `[[target#section]]`, `[[target|label]]`. Never across a line
 * break — neither does Obsidian.
 */
const LINK = /\[\[([^[\]|\n]*)(?:\|([^[\]\n]*))?\]\]/g;

/** The title and the section a target names, split at the first `#`. */
export function splitTarget(raw: string): { target: string; heading: string | null } {
  const at = raw.indexOf('#');
  if (at < 0) return { target: raw.trim(), heading: null };
  return { target: raw.slice(0, at).trim(), heading: raw.slice(at + 1).trim() || null };
}

/** Every link in a page's markdown, in the order they are read. */
export function wikiLinks(source: string): WikiLink[] {
  const text = withoutCode(String(source ?? ''));
  const found: WikiLink[] = [];
  for (const match of text.matchAll(LINK)) {
    const { target, heading } = splitTarget(match[1]);
    // `[[#Day one]]` is a section of this page and names no other; `[[  ]]` and
    // `[[#]]` name nothing at all.
    if (!target && !heading) continue;
    found.push({
      target,
      heading,
      label: match[2] === undefined ? null : match[2].trim() || null,
      at: match.index,
      end: match.index + match[0].length,
    });
  }
  return found;
}

/**
 * The id a heading carries, and the fragment a link to it has to spell.
 *
 * Shared rather than computed on both sides, because a slug that two functions
 * disagree about is a link that goes nowhere and nothing that says so. Letters
 * and digits survive as they were written — `Über uns` is `über-uns`, not
 * `uber-uns` — because folding an umlaut is a decision about somebody's
 * language, and the fragment is percent-encoded by the browser anyway.
 */
export const headingSlug = (text: string): string =>
  String(text ?? '')
    .replace(/`([^`]*)`/g, '$1')
    .replace(LINK, (_, raw: string, alias: string | undefined) =>
      (alias?.trim() || splitTarget(raw).target || splitTarget(raw).heading || ''))
    .replace(/!?\[([^\]]*)\]\([^)]*\)/g, '$1')
    .replace(/[*_~]/g, '')
    .trim()
    .toLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, '-')
    .replace(/^-+|-+$/g, '');

/**
 * Look a title up among these pages.
 *
 * Two pages may carry the same title — nothing forbids it and people do it,
 * usually one per project. The rule is the least surprising one available:
 * whichever was written first wins, so a link that resolved yesterday resolves
 * to the same page today, and creating a second `Notes` does not silently move
 * every link in the workspace. An exact-case match beats a folded one, which is
 * how `[[FAQ]]` and `[[Faq]]` can be told apart when somebody has both.
 */
export function pageResolver<T extends LinkablePage>(pages: readonly T[]): (target: string) => T | undefined {
  const exact = new Map<string, T>();
  const folded = new Map<string, T>();
  const older = (a: T, b: T | undefined): boolean => !b || (a.created_at ?? 0) < (b.created_at ?? 0);
  for (const page of pages) {
    const title = String(page.title ?? '').trim();
    if (!title) continue;
    if (older(page, exact.get(title))) exact.set(title, page);
    const key = pageKey(title);
    if (older(page, folded.get(key))) folded.set(key, page);
  }
  return (target: string) => exact.get(target.trim()) ?? folded.get(pageKey(target));
}

/** Who points at whom, once, for a whole workspace. */
export interface LinkGraph {
  /** Page id → the pages it links to, each named once and in reading order. */
  out: Map<string, string[]>;
  /** Page id → the pages that link to it. */
  in: Map<string, string[]>;
  /** A title nothing resolves to → the pages asking for it. The wiki's to-write list. */
  missing: Map<string, string[]>;
}

/**
 * The whole web in one pass.
 *
 * Built rather than queried because the caller already has the pages: the
 * client syncs every page it may read, and the server holds them in one table.
 * A link out of a page nobody may see is simply not in the list that was handed
 * in — which is what keeps the visibility rule in the one place that owns it
 * instead of copied in here.
 *
 * A page never links to itself: `[[This page]]` written on the page it names is
 * a note to the reader, not an edge, and drawing it would put every such page
 * in its own backlinks.
 */
export function linkGraph<T extends LinkablePage>(pages: readonly T[]): LinkGraph {
  const resolve = pageResolver(pages);
  const graph: LinkGraph = { out: new Map(), in: new Map(), missing: new Map() };
  const push = (map: Map<string, string[]>, key: string, value: string) => {
    const list = map.get(key);
    if (!list) map.set(key, [value]);
    else if (!list.includes(value)) list.push(value);
  };
  for (const page of pages) {
    for (const link of wikiLinks(page.content ?? '')) {
      // A link to a section of the page it is written on names nothing else.
      if (!link.target) continue;
      const found = resolve(link.target);
      if (!found) push(graph.missing, pageKey(link.target), page.id);
      else if (found.id !== page.id) {
        push(graph.out, page.id, found.id);
        push(graph.in, found.id, page.id);
      }
    }
  }
  return graph;
}

/**
 * The passage around a link, for a backlink somebody can read without opening
 * the page it came from.
 *
 * A list of titles answers "who links here" and nothing else; the question
 * people actually have is *why*, and the sentence containing the link is the
 * cheapest honest answer. The link itself is kept as its label rather than cut
 * out, so the quote reads the way the paragraph does.
 */
export function linkContext(source: string, target: string, max = 180): string | null {
  const text = withoutCode(String(source ?? ''));
  const key = pageKey(target);
  for (const link of wikiLinks(source)) {
    if (pageKey(link.target) !== key) continue;
    // The paragraph the link sits in, not the whole page: a blank line is where
    // one thought stops, and quoting past it quotes something else.
    const before = text.lastIndexOf('\n\n', link.at);
    const after = text.indexOf('\n\n', link.end);
    const paragraph = source.slice(before < 0 ? 0 : before + 2, after < 0 ? source.length : after);
    const shown = paragraph
      .replace(LINK, (_, raw: string, label: string | undefined) =>
        (label?.trim() || splitTarget(raw).target || splitTarget(raw).heading || ''))
      .replace(/[#>*_~`]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();
    if (!shown) continue;
    return shown.length > max ? `${shown.slice(0, max)}…` : shown;
  }
  return null;
}

/**
 * Every `[[from]]` in this source rewritten to `[[to]]`, or the source
 * unchanged when there was nothing to do.
 *
 * Renaming a page breaks every link to it, and a wiki where that happens twice
 * is a wiki people stop linking in. The rewrite is deliberately narrow: only
 * links whose target resolves to the old title by `pageKey`, and only the
 * target — an author who wrote `[[Onboarding|how we start]]` chose those words
 * about the page, and a rename is not a reason to take them away.
 *
 * `null` means the new title cannot be linked to at all (see `linkableTitle`).
 * Returned rather than thrown, and rather than quietly writing a broken link,
 * because the caller is the only one that can say so to the person renaming.
 */
export function renameLinks(source: string, from: string, to: string): string | null {
  if (!linkableTitle(to)) return null;
  const text = String(source ?? '');
  const key = pageKey(from);
  const next = to.trim();
  const links = wikiLinks(text).filter((link) => link.target && pageKey(link.target) === key);
  if (!links.length) return text;
  let out = '';
  let cursor = 0;
  for (const link of links) {
    out += text.slice(cursor, link.at);
    // The section and the alias both survive: one is where in the page, the
    // other is what the author called it, and a rename is about neither.
    const inside = `${next}${link.heading ? `#${link.heading}` : ''}`;
    out += link.label === null ? `[[${inside}]]` : `[[${inside}|${link.label}]]`;
    cursor = link.end;
  }
  return out + text.slice(cursor);
}

/**
 * A slug maker that remembers what it has already given out.
 *
 * The dedup lives here rather than in each caller because there are two of
 * them — the renderer, writing the ids, and the outline, writing the links to
 * them — and a document with two sections called Notes is the case where a
 * disagreement between them stops being theoretical.
 */
export function slugCounter(): (text: string) => string {
  const seen = new Map<string, number>();
  return (text: string) => {
    const base = headingSlug(text) || 'section';
    const at = (seen.get(base) ?? 0) + 1;
    seen.set(base, at);
    return `${base}${at > 1 ? `-${at}` : ''}`;
  };
}

/** One heading in a page, as an outline beside the text draws it. */
export interface Heading {
  /** 1 for `#`, 6 for `######`. */
  level: number;
  text: string;
  /** Without the document's prefix — the caller knows that. */
  slug: string;
}

/**
 * The headings of a page, in the order they are written.
 *
 * Read off the source rather than off the rendered HTML, so an outline can be
 * drawn without rendering the document twice — and so the same function answers
 * on the server, where there is no DOM to query. Code is skipped, because a
 * `# comment` inside a shell block is not a section and an outline full of
 * those is an outline nobody reads.
 *
 * The `Setext` form — a row of `=` or `-` under a line — counts too, because
 * the renderer treats it as a heading and an outline that disagreed with the
 * page would be worse than no outline.
 */
/**
 * Whether the line at `at` opens a section, and how many lines it takes.
 *
 * `lines` must be the source with its code blanked out — `withoutCode` — or a
 * `# comment` in a shell example is a chapter.
 *
 * Extracted because two callers ask the same question and got two answers:
 * `outlineOf` read the `Setext` form and `splitByHeadings` did not, so a
 * document written that way drew a full outline and imported as a single page.
 * A rule that two functions apply is a rule that will drift; asked once, it
 * cannot.
 *
 * `spans` is 2 for the underlined form, because its second line belongs to the
 * heading and a caller slicing the document has to know not to leave it behind.
 */
export function headingAt(lines: readonly string[], at: number): { level: number; text: string; spans: number } | null {
  const line = lines[at] ?? '';
  const hash = /^ {0,3}(#{1,6})\s+(.*?)(?:\s+#+)?\s*$/.exec(line);
  if (hash) return { level: hash[1].length, text: hash[2].trim(), spans: 1 };

  const under = /^ {0,3}(=+|-+)\s*$/.exec(lines[at + 1] ?? '');
  // A row of dashes under a blank line is a rule, and under a list item it is
  // that item — the renderer only reads it as an underline for a paragraph.
  if (!under || !line.trim() || /^\s*([-*+]|\d+[.)])\s/.test(line) || /^\s*>/.test(line)) return null;
  return { level: under[1][0] === '=' ? 1 : 2, text: line.trim(), spans: 2 };
}

export function outlineOf(source: string): Heading[] {
  const lines = withoutCode(String(source ?? '')).split('\n');
  const slug = slugCounter();
  const out: Heading[] = [];
  for (let at = 0; at < lines.length; at += 1) {
    const found = headingAt(lines, at);
    if (!found) continue;
    out.push({ level: found.level, text: found.text, slug: slug(found.text) });
    at += found.spans - 1;
  }
  return out;
}
